import { useNavigate } from '@tanstack/react-router';
import useResponsive from 'ahooks/es/useResponsive';
import clsx from 'clsx';
import { type FC, useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

import { faBookmark } from '@fortawesome/pro-solid-svg-icons/faBookmark';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';

import { DealerPrimaryContactMethod } from '@furality/fox-client/models';
import { ReportError, useMutation, useQuery } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import ExternalRedirectLink from '@/components/ExternalRedirectLink.tsx';
import FontAwesomeIcon from '@/components/FontAwesomeIcon.tsx';
import LinkButton from '@/components/LinkButton.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import TooltipHelper from '@/components/TooltipHelper.tsx';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useTitle from '@/lib/useTitle.ts';

import BackwallVideo from './BackwallVideo.tsx';
import style from './Booth.module.scss';
import { hasContactMethod, useUpdateDealerMetrics } from './ContactMethod.helpers.ts';
import ContactMethod from './ContactMethod.tsx';
import DealerLocationInfo from './DealerLocationInfo.tsx';
import DealerShareToggleButton from './DealerShareToggleButton.tsx';

const allPossibleContactMethods = Object.values(DealerPrimaryContactMethod);

type BoothProps = {
  activeBoothId: number;
};

const Booth: FC<BoothProps> = ({ activeBoothId }) => {
  const navigate = useNavigate();

  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const activeBooth = useQuery({
    queryKey: [QUERY_KEYS.Dealer, activeBoothId],
    queryFn: async () => {
      const response = await fox.value.dealerFindOne({
        params: {
          boothId: activeBoothId,
        },
      });
      switch (response.status) {
        case 200:
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
  });

  const boothAnimation = 'animate__animated animate__fadeInRight animate__faster';
  const updateDealerMetrics = useUpdateDealerMetrics();

  // need to hardcode these as not provided in api

  // dealer's den only
  const leftImageUrl = useMemo(
    () =>
      `https://media.cdn.furality.online/dealers/f8/booth/${activeBoothId}/booth_walls_left.webp`,
    [activeBoothId],
  );
  const rightImageUrl = useMemo(
    () =>
      `https://media.cdn.furality.online/dealers/f8/booth/${activeBoothId}/booth_walls_right.webp`,
    [activeBoothId],
  );

  const bannerImageUrl = useMemo(
    () => `https://media.cdn.furality.online/dealers/f8/booth/${activeBoothId}/banner.webp`,
    [activeBoothId],
  );

  const { sm, lg } = useResponsive();
  const { t } = useTranslation(['common', 'dealers']);

  useTitle('f7.explore.dealers', activeBooth.data?.name);

  const bookmarkBooth = useMutation({
    mutationFn: (id: number) =>
      fox.value
        .dealerBookmark({
          params: {
            boothId: id,
          },
        })
        .then(() => activeBooth.refetch()),
  });

  const unbookmarkBooth = useMutation({
    mutationFn: (id: number) =>
      fox.value
        .dealerUnbookmark({
          params: {
            boothId: id,
          },
        })
        .then(() => activeBooth.refetch()),
  });

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setShowLightbox(true);
  };

  const lightboxImages = useMemo(() => {
    const images = [
      {
        src: leftImageUrl,
      },
      {
        src: rightImageUrl,
      },
    ];

    if (activeBooth.data?.boothVideo !== null && activeBooth.data?.boothVideo.endsWith('.png')) {
      images.push({
        src: activeBooth.data.boothVideo,
      });
    }

    return images;
  }, [leftImageUrl, rightImageUrl, activeBooth.data]);

  return (
    <>
      {activeBooth.isLoading && <PageLoading />}
      {activeBooth.isSuccess && (
        <div
          className={`bg-dark ${boothAnimation}`}
          style={{ position: 'relative', zIndex: 1000001 }}
        >
          <Row>
            <Col className="position-relative">
              <div
                style={{
                  position: 'absolute',
                  top: lg ? '24px' : 'unset',
                  left: lg ? '32px' : '28px',
                  bottom: lg ? 'unset' : '-24px',
                  zIndex: '1000',
                }}
              >
                <Button
                  variant="primary"
                  className={clsx('d-flex', sm ? 'p-4' : 'p-3')}
                  size={sm ? 'xl' : undefined}
                  rounded
                  aria-label={t('shared.go_back')}
                  title={t('shared.go_back')}
                  style={{
                    boxShadow: '0px 12px 15px rgb(0 0 0 / 60%)',
                  }}
                  onClick={() => {
                    if (
                      document.referrer.length > 0 &&
                      document.referrer.startsWith(window.location.origin)
                    ) {
                      window.history.back();
                    } else {
                      void navigate({
                        to: '/explore/dealers',
                        hash: `booth-${activeBoothId}`,
                      });
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                </Button>
              </div>
              <div
                className="position-absolute"
                style={{
                  top: 'unset',
                  right: lg ? 'unset' : '28px',
                  left: lg ? '32px' : 'unset',
                  bottom: lg ? '-52px' : '-35px',
                  fontSize: lg ? '3rem' : '2rem',
                  zIndex: '1000',
                  display: 'inline',
                }}
              >
                {activeBooth.data.bookmarked ? (
                  <TooltipHelper
                    id={`booth-view-unbookmark-${activeBooth.data.boothId}`}
                    title={t('dealers:booth.bookmark.remove.tooltip')}
                    child={
                      // TODO: tooltip
                      <button
                        type="button"
                        aria-label={t('dealers:booth.bookmark.remove.tooltip')}
                        className="text-warning mr-2 align-middle unstyled-button"
                        onClick={() => unbookmarkBooth.mutate(activeBooth.data.boothId)}
                      >
                        <FontAwesomeIcon
                          className={style['bookmarkIconButton']}
                          icon={faBookmark}
                        />
                      </button>
                    }
                  />
                ) : (
                  <TooltipHelper
                    id={`booth-view-bookmark-${activeBooth.data.boothId}`}
                    title={t('dealers:booth.bookmark.add.tooltip')}
                    child={
                      // TODO: tooltip
                      <button
                        aria-label={t('dealers:booth.bookmark.add.tooltip')}
                        className="unstyled-button text-secondary mr-2 align-middle"
                        onClick={() => bookmarkBooth.mutate(activeBooth.data.boothId)}
                      >
                        <FontAwesomeIcon
                          className={style['bookmarkIconButton']}
                          icon={faBookmark}
                        />
                      </button>
                    }
                  />
                )}
              </div>

              <img
                className="img-fluid"
                src={bannerImageUrl}
                alt={`Banner for ${activeBooth.data.name}`}
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col lg={6} md={12} className="pr-lg-0">
              <Card className="bg-fox-gradient-blue hover-shadow-lg text-white h-100 px-3 pt-3">
                <div className="d-flex my-3 text-center">
                  <div className="m-auto">
                    <h1 className="text-white">
                      {activeBooth.data.name}{' '}
                      {activeBooth.data.ownerIsRequester && (
                        <DealerShareToggleButton
                          dealerId={activeBoothId}
                          ownerDisplayLocation={activeBooth.data.ownerDisplayLocation}
                        />
                      )}
                    </h1>
                    {activeBooth.data.ownerDisplayName && (
                      <p>
                        Booth by <strong>{activeBooth.data.ownerDisplayName}</strong>
                      </p>
                    )}
                    {activeBooth.data.boothId && (
                      <p>
                        <TooltipHelper
                          id="booth-detail-locate"
                          title={t('dealers:booth.locate.tooltip')}
                          child={
                            <LinkButton
                              to="/explore/dealers"
                              search={{ locate: activeBoothId }}
                              variant="outline-white"
                              rounded
                              size="sm"
                            >
                              Booth #{activeBooth.data.boothId}
                            </LinkButton>
                          }
                        />
                      </p>
                    )}

                    <div style={{ overflowWrap: 'anywhere' }}>
                      {activeBooth.data.tags.map((tag) => (
                        <span
                          className="badge badge-pill badge-dark mr-1 d-inline-block"
                          style={{ fontSize: '12px' }}
                          key={tag}
                        >
                          {
                            t(
                              `dealers:tags.${tag.toLowerCase().replaceAll(' ', '-')}` as Parameters<
                                typeof t
                              >[0],
                            ) as string
                          }
                        </span>
                      ))}
                    </div>
                    <br />
                    {allPossibleContactMethods.map(
                      (contactMethod) =>
                        hasContactMethod(activeBooth.data, contactMethod) && (
                          <ContactMethod
                            dealer={activeBooth.data}
                            contactMethod={contactMethod}
                            key={contactMethod}
                          />
                        ),
                    )}
                    <br />
                    <br />
                  </div>
                </div>
                <div className={style['imageGallery']}>
                  <button type="button" onClick={() => openLightbox(0)} className="unstyled-button">
                    <img
                      data-fancybox="gallery"
                      className="img-fluid"
                      src={leftImageUrl}
                      style={{ cursor: 'pointer' }}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                      alt={`Left side banner of booth for ${activeBooth.data.name}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => openLightbox(1)}
                    className="unstyled-button mt-2"
                  >
                    <img
                      data-fancybox="gallery"
                      className="img-fluid"
                      src={rightImageUrl}
                      style={{ cursor: 'pointer' }}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                      alt={`Right side banner of booth for ${activeBooth.data.name}`}
                    />
                  </button>
                  {activeBooth.data.boothVideo !== null &&
                    activeBooth.data.boothVideo.endsWith('.mp4') && (
                      <BackwallVideo
                        src={activeBooth.data.boothVideo}
                        alt={`Promotional video for ${activeBooth.data.name}`}
                        className="mt-2"
                      />
                    )}
                  {activeBooth.data.boothVideo !== null &&
                    activeBooth.data.boothVideo.endsWith('.png') && (
                      <button
                        type="button"
                        onClick={() => openLightbox(2)}
                        className="unstyled-button mt-2"
                      >
                        <img
                          data-fancybox="gallery"
                          className="img-fluid"
                          src={activeBooth.data.boothVideo}
                          style={{ cursor: 'pointer' }}
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                          alt={`booth wall for ${activeBooth.data.name}`}
                        />
                      </button>
                    )}
                  <Lightbox
                    open={showLightbox}
                    close={() => setShowLightbox(false)}
                    index={lightboxIndex}
                    slides={lightboxImages}
                    plugins={[Zoom]}
                  />
                </div>
              </Card>
            </Col>
            <Col lg={6} md={12}>
              <div className="lead py-3 px-3 text-white">
                <DealerLocationInfo dealer={activeBooth.data} />
                <ReactMarkdown
                  components={{
                    a: (props) => (
                      <ExternalRedirectLink
                        href={props.href}
                        hasMatureContent={activeBooth.data.matureContent}
                        onClick={() => props.href && updateDealerMetrics(activeBoothId, props.href)}
                        className="brighter-link text-underline"
                      >
                        {props.children}
                      </ExternalRedirectLink>
                    ),
                    img: (props) => <img className="img-fluid" alt={props.alt} src={props.src} />,
                  }}
                >
                  {activeBooth.data.longDescription}
                </ReactMarkdown>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default Booth;
